import { AxiosResponse } from "axios";
import {
  CancelSubscriptionApiResponse,
  CancelSubscriptionRequestPayload,
  DownloadSubscriptionInvoiceApiResponse,
  ManageLocalProtienApiResponse,
  ManageLocalProtienPayload,
  ManageRefundRequestApiResponse,
  ManageRefundRequestPayload,
  UpdateSubscriptionDaysApiResponse,
  UpdateSubscriptionDaysRequestPayload,
  UpdateSubscriptionStartDateApiResponse,
  UpdateSubscriptionStartDateRequestPayload,
} from "app/types";
import { getJwtDetails } from "./authService";
import axios from "./config";
import {
  CancelSubscriptionDetailsApiResponse,
  ClientCustomMealsApiResponse,
  CustomMealDetailsApiResponse,
  GetDeliveryShiftsApiResponse,
  GetPlannedOrdersApiResponse,
  GetSubscriptionDetailApiResponse,
  MealHistoryApiResponse,
  PlannedOrderLogsApiResponse,
  RefundRequestsApiResponse,
  RefundStatus,
  SubscriptionHistoryApiResponse,
  UpsertClientCustomMealApiResponse,
  UpsertClientCustomMealPayload,
} from "app/types/subscriptionsManager";

export const getSubscriptionsAPI = (payload: any) =>
  axios.get(`/v2/subscriptions${payload}`);

export const updateStatusAPI = (
  payload: { progress: string },
  orderId: number,
) => axios.post(`/express-orders/${orderId}/events`, payload);

export const getSubscriptionDetailAPI = (
  id: number,
): Promise<AxiosResponse<GetSubscriptionDetailApiResponse>> =>
  axios.get<GetSubscriptionDetailApiResponse>(`/subscriptions/${id}?v=2`);

export const getSubscriptionLimitAPI = (storeId: number) =>
  axios.get(`/stores/${storeId}/limits`);

export const orderCreateAPI = (payload: any) => {
  const { userId }: any = getJwtDetails() || {};
  return axios.post(`/users/${userId}/Orders`, payload);
};

export const orderUpdateAPI = (payload: any, orderId: number) => {
  return axios.put(`orders/${orderId}`, payload);
};

export const getOrderAPI = (orderId: any) => {
  const { userId }: any = getJwtDetails() || {};
  return axios.get(`/users/${userId}/Orders/${orderId}`);
};

export const getOrderRequestAPI = (orderId: any) => {
  return axios.get(`/orders/${orderId}`);
};

export const checkoutsAPI = (payload: any, orderId: number) => {
  const { userId }: any = getJwtDetails() || {};
  return axios.post(`/v3/users/${userId}/Orders/${orderId}/payments`, payload);
};

export const applyCouponCodeAPI = (
  payload: {
    CouponCode: string;
    v: string;
  },
  orderId: number,
) => {
  const { userId }: any = getJwtDetails() || {};
  return axios.post(`/users/${userId}/Orders/${orderId}/promotions`, payload);
};

export const deleteCouponCodeAPI = (orderId: any) => {
  const { userId }: any = getJwtDetails() || {};
  return axios.delete(`/users/${userId}/Orders/${orderId}/promotions?v=1.0`);
};

export const getDeliveryShiftsAPI = (
  storeId: number,
): Promise<AxiosResponse<GetDeliveryShiftsApiResponse>> =>
  axios.get<GetDeliveryShiftsApiResponse>(
    `/stores/${storeId}/delivery-shifts?v=1.0`,
  );

export const getSummaryOrdersAPI = (payload: any) => {
  const { subscriptionId, fromDate, toDate } = payload;
  return axios.get(
    `subscriptions/${subscriptionId}/summary-orders?fromDate=${fromDate}&toDate=${toDate}`,
  );
};

export const getPackageMealAPI = (payload: any) => {
  const { packageId, date, portionId, query } = payload;
  return axios.get(
    `v2/packages/${packageId}/meals/${date}?portionId=${portionId}&v=2.1${query}`,
  );
};
export const getCorePackageMealAPI = (payload: any) => {
  const { clientId, date, subscriptionId, query } = payload;
  return axios.get(
    `/clients/${clientId}/subscriptions/${subscriptionId}/daily-menus/${date}${query}`,
  );
};

export const getPlannedOrdersAPI = (payload: {
  subscriptionId: number;
  date: string;
}): Promise<AxiosResponse<GetPlannedOrdersApiResponse>> => {
  const { subscriptionId, date } = payload;
  return axios.get<GetPlannedOrdersApiResponse>(
    `subscriptions/${subscriptionId}/planned-orders/${date}`,
  );
};

export const putDeliveryScheduleAPI = (payload: any) => {
  const { clientId, data } = payload;
  return axios.put(`clients/${clientId}/delivery-schedules`, data);
};
export const getDeliveryScheduleAPI = (clientId: any) => {
  return axios.get(`clients/${clientId}/delivery-schedules`);
};

export const createPlannedOrderAPI = (data: any) => {
  const { subscriptionId, payload } = data;
  return axios.post(`subscriptions/${subscriptionId}/planned-orders`, payload);
};

export const addPlannedItemAPI = (data: any) => {
  const { subscriptionId, payload, plannedId } = data;
  return axios.post(
    `subscriptions/${subscriptionId}/planned-orders/${plannedId}/planned-items`,
    payload,
  );
};

export const getPauseDaysAPI = (subscriptionId: number) => {
  return axios.get(`Subscriptions/${subscriptionId}/paused-subscriptions`);
};

export const pauseSubscriptionAPI = (data: any) => {
  const { clientid, payload } = data;
  return axios.post(`clients/${clientid}/paused-subscriptions`, payload);
};

interface Freeze {
  clientId: number;
  payload: {
    type: "Freeze";
    startAt: string;
  };
}

export const freezeSubscriptionAPI = (data: Freeze) => {
  const { clientId, payload } = data;
  return axios.post(`clients/${clientId}/paused-subscriptions`, payload);
};
export const unfreezeSubscriptionAPI = (payload: any) => {
  const { clientId, startAt } = payload;
  return axios.delete(`clients/${clientId}/paused-subscriptions/${startAt}`);
};
export const connectAppAPI = (payload: {
  clientId: number;
  password: string;
  application: string;
}) => {
  return axios.post(`client-users`, payload);
};

export const cashPaymentAPI = (data: any) => {
  const { orderId, paymentId } = data;
  return axios.post(
    `v2/orders/${orderId}/paymentRequests/${paymentId}/manualPayments`,
    {},
  );
};

export const updatePlannedOrderAPI = (payload: any) => {
  const { subscriptionId, plannedId, data } = payload;

  return axios.patch(
    `subscriptions/${subscriptionId}/planned-orders/${plannedId}`,
    data,
  );
};

export const getRequestAPI = (query: string) =>
  axios.get(`/subscription-requests${query}`);

export const getPortionsAPI = () => axios.get(`/portions`);

export const updateSubscriptionMealAPI = (payload: any) => {
  const { subscriptionId, meals } = payload;
  const body = { meals: meals };
  return axios.put(`subscriptions/${subscriptionId}/meals`, body);
};

export const getPlannedOrderLogsApi = (
  plannedOrderId: number,
  query = "",
): Promise<AxiosResponse<PlannedOrderLogsApiResponse>> =>
  axios.get<PlannedOrderLogsApiResponse>(
    `/planned-orders/${plannedOrderId}/logs${query}`,
  );

export const getMealHistoryApi = (
  plannedOrderId: number,
): Promise<AxiosResponse<MealHistoryApiResponse>> =>
  axios.get<MealHistoryApiResponse>(
    `/planned-orders/${plannedOrderId}/meal-history`,
  );

export const updateSubscriptionDaysApi = (
  id: number,
  paylaod: UpdateSubscriptionDaysRequestPayload,
): Promise<AxiosResponse<UpdateSubscriptionDaysApiResponse>> => {
  return axios.put<UpdateSubscriptionDaysApiResponse>(
    `/subscriptions/${id}/subscription-days`,
    paylaod,
  );
};

export const updateSubscriptionStartDateApi = (
  id: number,
  paylaod: UpdateSubscriptionStartDateRequestPayload,
): Promise<AxiosResponse<UpdateSubscriptionStartDateApiResponse>> => {
  return axios.put<UpdateSubscriptionStartDateApiResponse>(
    `/subscriptions/${id}/start-date`,
    paylaod,
  );
};
export const downloadSubscriptionInvoiceApi = (
  id: number,
): Promise<AxiosResponse<DownloadSubscriptionInvoiceApiResponse>> => {
  return axios.get<DownloadSubscriptionInvoiceApiResponse>(
    `/orders/${id}/invoices`,
  );
};
export const getSubscriptionLogsApi = (
  subscriptionId: number,
  query = "",
): Promise<AxiosResponse<SubscriptionHistoryApiResponse>> => {
  return axios.get<SubscriptionHistoryApiResponse>(
    `/subscriptions/${subscriptionId}/logs${query}`,
  );
};

export const getClientPreferenceLogsApi = (
  clientId: number,
  query = "",
): Promise<AxiosResponse<SubscriptionHistoryApiResponse>> => {
  return axios.get<SubscriptionHistoryApiResponse>(
    `/clients/${clientId}/logs${query}`,
  );
};

export const cancelSubscriptionApi = (
  subscriptionId: number,
  paylaod: CancelSubscriptionRequestPayload,
): Promise<AxiosResponse<CancelSubscriptionApiResponse>> => {
  return axios.post<CancelSubscriptionApiResponse>(
    `/subscriptions/${subscriptionId}/status`,
    paylaod,
  );
};

export const subscriptionRefundRequestApi = (
  clientId: number,
  payload: ManageRefundRequestPayload,
): Promise<AxiosResponse<ManageRefundRequestApiResponse>> => {
  return axios.post<ManageRefundRequestApiResponse>(
    `/clients/${clientId}/refund-requests`,
    payload,
  );
};

export const getRefundRequestsApi = (
  clientId: number,
  query = "",
): Promise<AxiosResponse<RefundRequestsApiResponse>> =>
  axios.get<RefundRequestsApiResponse>(
    `/clients/${clientId}/refund-requests${query}`,
  );

export const updateRefundRequestStatusApi = (payload: {
  clientId: number;
  refundRequestId: number;
  apiBody: {
    status: RefundStatus;
    RejectionExplaination?: string;
    PaymentMethod?: string;
  };
}) =>
  axios.put(
    `/clients/${payload.clientId}/refund-requests/${payload.refundRequestId}`,
    payload.apiBody,
  );

export const updateLocalProtien = (
  subscriptionId: number,
  paylaod: ManageLocalProtienPayload,
): Promise<AxiosResponse<ManageLocalProtienApiResponse>> => {
  return axios.patch<ManageLocalProtienApiResponse>(
    `/subscriptions/${subscriptionId}/protein`,
    paylaod,
  );
};

export const getCancelSubscriptionDetailsApi = (
  subscriptionId: number,
): Promise<AxiosResponse<CancelSubscriptionDetailsApiResponse>> =>
  axios.get<CancelSubscriptionDetailsApiResponse>(
    `/subscriptions/${subscriptionId}/cancellation-details`,
  );

export const getCustomMealDetailsApi = (
  storeId: number,
  mealId: number,
  query = "",
): Promise<AxiosResponse<CustomMealDetailsApiResponse>> =>
  axios.get<CustomMealDetailsApiResponse>(
    `/stores/${storeId}/meals/${mealId}/details${query}`,
  );

export const getClientCustomMealsApi = (
  storeId: number,
  clientId: number,
  mealId: number,
  query = "",
): Promise<AxiosResponse<ClientCustomMealsApiResponse>> =>
  axios.get<ClientCustomMealsApiResponse>(
    `/stores/${storeId}/clients/${clientId}/custom-meals/${mealId}${query}`,
  );

export const postClientCustomMealApi = (
  storeId: number,
  clientId: number,
  payload: UpsertClientCustomMealPayload,
): Promise<AxiosResponse<UpsertClientCustomMealApiResponse>> =>
  axios.post<UpsertClientCustomMealApiResponse>(
    `/stores/${storeId}/clients/${clientId}/custom-meals`,
    payload,
  );

export const putClientCustomMealApi = (
  storeId: number,
  clientId: number,
  clientCustomMealId: number,
  payload: UpsertClientCustomMealPayload,
): Promise<AxiosResponse<UpsertClientCustomMealApiResponse>> =>
  axios.put<UpsertClientCustomMealApiResponse>(
    `/stores/${storeId}/clients/${clientId}/custom-meals/${clientCustomMealId}`,
    payload,
  );

export const updateEnableCustomMealApi = (
  subscriptionId: number,
  payload: { enableCustomMeal: boolean },
) =>
  axios.patch(`/subscriptions/${subscriptionId}/custom-meals`, payload);
